import { useEffect, useMemo, useState } from "react";
import { formatMoney } from "../lib/format";
import type { CartLineSelection } from "../types";
import type { Product } from "@cliqsuite/db/schemas";
import { useRows } from "../lib/db";

export interface CustomizerModalProps {
  product: Product;
  currency: { code: string; precision: 0 | 2 | 3 };
  onClose: () => void;
  onConfirm: (qty: number, selections: CartLineSelection[], unitPrice: number, note?: string) => void;
}

/** Mirrors mockup's ITEM CUSTOMIZER — real catalog.product_variant_groups/options, read from the local PowerSync copy. */
export function CustomizerModal({ product, currency, onClose, onConfirm }: CustomizerModalProps) {
  const fmt = (v: number) => formatMoney(v, { currencyCode: currency.code, precision: currency.precision });

  const groupsResult = useRows("product_variant_groups", 
    "SELECT * FROM product_variant_groups WHERE product_id = $1",
    [product.id],
  );
  const groups = groupsResult?.rows;
  const sortedGroups = useMemo(() => (groups ?? []).slice().sort((a, b) => a.sort_order - b.sort_order), [groups]);
  const groupIds = useMemo(() => sortedGroups.map((g) => g.id), [sortedGroups]);

  const optionsResult = useRows(
    "product_variant_options",
    "SELECT * FROM product_variant_options WHERE group_id IN (SELECT value FROM json_each($1))",
    [JSON.stringify(groupIds)],
  );
  const allOptions = optionsResult?.rows;
  const optionsByGroup = useMemo(() => {
    const m = new Map<string, typeof allOptions>();
    for (const g of sortedGroups) m.set(g.id, []);
    for (const o of (allOptions ?? []).filter((o) => o.is_active).sort((a, b) => a.sort_order - b.sort_order)) {
      const list = m.get(o.group_id);
      if (list) list.push(o);
    }
    return m;
  }, [allOptions, sortedGroups]);

  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState<Record<string, string | string[]>>({});

  // Single-select groups default to their first option, matching the mockup — multi-select groups start empty.
  useEffect(() => {
    setSel((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const g of sortedGroups) {
        if (next[g.id] !== undefined) continue;
        const opts = optionsByGroup.get(g.id) ?? [];
        next[g.id] = g.max_selections === 1 ? (opts[0]?.id ?? "") : [];
        changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedGroups.map((g) => g.id).join(",")]);

  const pick = (groupId: string, optionId: string, multi: boolean, max: number) => {
    setSel((prev) => {
      if (!multi) return { ...prev, [groupId]: optionId };
      const cur = Array.isArray(prev[groupId]) ? (prev[groupId] as string[]) : [];
      if (cur.includes(optionId)) return { ...prev, [groupId]: cur.filter((id) => id !== optionId) };
      if (max > 0 && cur.length >= max) return prev;
      return { ...prev, [groupId]: [...cur, optionId] };
    });
  };

  const selections: CartLineSelection[] = useMemo(() => {
    const out: CartLineSelection[] = [];
    for (const g of sortedGroups) {
      const opts = optionsByGroup.get(g.id) ?? [];
      const v = sel[g.id];
      const ids = Array.isArray(v) ? v : v ? [v] : [];
      for (const id of ids) {
        const opt = opts.find((o) => o.id === id);
        if (opt) out.push({ optionId: opt.id, groupLabel: g.name, label: opt.label, priceDelta: opt.price_delta });
      }
    }
    return out;
  }, [sel, sortedGroups, optionsByGroup]);

  const unitPrice = product.base_price + selections.reduce((s, o) => s + o.priceDelta, 0);
  const missingRequired = sortedGroups.some((g) => {
    if (!g.is_required) return false;
    const v = sel[g.id];
    return Array.isArray(v) ? v.length === 0 : !v;
  });

  const [note, setNote] = useState("");
  const from = sortedGroups.length ? "From " : "";

  return (
    <>
      <div className="pos-drawer-dim" onClick={onClose} />
      <aside role="dialog" aria-modal="true" aria-label={`${product.name} options`} className="pos-options">
        <div className="pos-options-head">
          <div className="pos-options-pic">
            {product.image_url ? <img src={product.image_url} alt="" /> : <span className="ms" aria-hidden="true">{product.emoji ? "" : "restaurant_menu"}</span>}
            {product.emoji && !product.image_url && <span className="pos-tile-emoji">{product.emoji}</span>}
          </div>
          <div>
            <b>{product.name}</b>
            <small className="num">
              {from}
              {fmt(product.base_price)}
            </small>
          </div>
          <button className="pos-sq-btn big" aria-label="Close" onClick={onClose}>
            <span className="ms" aria-hidden="true">close</span>
          </button>
        </div>
        <div className="pos-options-body">
          {sortedGroups.map((g) => {
            const opts = optionsByGroup.get(g.id) ?? [];
            const multi = g.max_selections !== 1;
            const v = sel[g.id];
            const selectedIds = Array.isArray(v) ? v : v ? [v] : [];
            return (
              <div key={g.id} role={multi ? "group" : "radiogroup"} aria-label={g.name}>
                <div className="pos-options-label">
                  {g.name}
                  <span>{g.is_required ? "Required" : multi ? (g.max_selections > 1 ? `Up to ${g.max_selections}` : "Optional") : "Pick one"}</span>
                </div>
                <div className="pos-options-row">
                  {opts.map((o) => {
                    const active = selectedIds.includes(o.id);
                    return (
                      <button key={o.id} role={multi ? "checkbox" : "radio"} aria-checked={active} className={active ? "on" : ""} onClick={() => pick(g.id, o.id, multi, g.max_selections)}>
                        {o.label}
                        {o.price_delta !== 0 && <span className="num">{o.price_delta > 0 ? "+" : "−"}{Math.abs(o.price_delta).toLocaleString()}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <label className="pos-options-note">
            <span className="ms" aria-hidden="true">sticky_note_2</span>
            <input aria-label="Note" placeholder="Add a note for the kitchen" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
        <div className="pos-options-foot">
          <div className="pos-stepper">
            <button aria-label="One less" disabled={qty <= 1} onClick={() => setQty((q) => Math.max(1, q - 1))}>
              <span className="ms" aria-hidden="true">remove</span>
            </button>
            <span className="num">{qty}</span>
            <button aria-label="One more" onClick={() => setQty((q) => q + 1)}>
              <span className="ms" aria-hidden="true">add</span>
            </button>
          </div>
          <button className="pos-options-add" disabled={missingRequired} onClick={() => onConfirm(qty, selections, unitPrice, note.trim() || undefined)}>
            {missingRequired ? "Pick the required options" : "Add to ticket"}
            {!missingRequired && <span className="num">{fmt(unitPrice * qty)}</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
